import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../../containers/ApiContainer';
import { useActiveFBOs } from '../../../containers/ActiveFBOContainer';

interface LocationUsageCount {
  location_id: string;
  location_type: 'hangar' | 'ramp';
  assignment_count: number;
}

interface LocationUsageCounts {
  hangarCounts: Map<string, number>;
  rampCounts: Map<string, number>;
}

/**
 * Hook for fetching location usage counts for quick-action button ordering.
 * Returns counts per hangar/ramp from trip_placement table.
 * Database aggregation handles large datasets efficiently.
 */
export function useLocationUsageCounts(): LocationUsageCounts {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const [counts, setCounts] = useState<LocationUsageCounts>({
    hangarCounts: new Map(),
    rampCounts: new Map(),
  });

  const fetchCounts = useCallback(async () => {
    if (!activeFBO?.id || !postgrest) return;

    try {
      const { data, error } = await postgrest.rpc('get_location_usage_counts');

      if (error) {
        console.error('Failed to fetch location usage counts:', error);
        return;
      }

      const hangarCounts = new Map<string, number>();
      const rampCounts = new Map<string, number>();

      (data as LocationUsageCount[] | null)?.forEach(row => {
        if (row.location_type === 'hangar') {
          hangarCounts.set(row.location_id, row.assignment_count);
        } else {
          rampCounts.set(row.location_id, row.assignment_count);
        }
      });

      setCounts({ hangarCounts, rampCounts });
    } catch (err) {
      console.error('Failed to fetch location usage counts:', err);
    }
  }, [activeFBO?.id, postgrest]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return counts;
}
